import { AppSyncFarm, MonitorVaults } from 'common/models/farm';
import React from 'react';
import { ScreenUtils } from 'utils/screenUtils';
import { FarmUtils } from 'utils/farmUtils';
import qs from 'qs';
import { TokenIcons } from '../TokenIcons/TokenIcons';
import FormatFloat from '../FormatPercent/FormatFloat';
import { OptionalOutboundLink } from '../OptionalOutboundLink';
import { CryptoUtils } from 'utils/cryptoUtils';
import Image from 'common/components/Image';
import { Link } from 'gatsby';
import useSelectedChain from 'hooks/useSelectedChain';
import { chains } from 'containers/WalletProvider/WalletProvider';
import VaultActionButton from './VaultActionButton';

type Props = {
  farm: AppSyncFarm;
  monitorVault: MonitorVaults | undefined;
  liquidity: number; // total liquidity of BASE TOKEN across all vaults in $
};

const VaultsInfoTablePosition: React.FC<Props> = (props) => {
  const { chainId } = useSelectedChain();

  const farm = props.farm;
  const monitorVault = props.monitorVault;
  const breakpoint = ScreenUtils.useBreakpoint();

  const baseToken = FarmUtils.getBaseToken(farm);
  const rewardToken = FarmUtils.getRewardToken(farm);

  const farmNetwork = chains.find((chain) => chain.id === farm.chainId)?.name;
  const farmNetworkIndicator = farmNetwork ? `(${farmNetwork})` : '';

  const depositLink = farmNetwork 
    ? `/vault?${qs.stringify({
        token: baseToken.address,
        pairedtoken: rewardToken.address,})}`
    : 'https://pancakeswap.finance/position-managers';

  const dexLink = farmNetwork 
    ? CryptoUtils.getUniswapLink(
      farm.chainId,
      monitorVault?.poolAddress as string)
    : 'https://pancakeswap.finance/position-managers';

  const linkTarget = farmNetwork ? 'self' : 'blank';

  const vaultIcon =
    rewardToken.displayName === 'ICHI' ? (
      <TokenIcons tokenNames={[baseToken.name]} />
    ) : (
      <TokenIcons tokenNames={[`${baseToken.name}_${rewardToken.name}`]} />
    );

  return (
    <tr
      className="table-border-bottom primary-text-color"
      key={farm.poolId}
    >
      {/* Vault name is linked to the Vault Deposit page on small screens,
        and to Etherscan/Polygonscan on bigger screens */}
      {![ScreenUtils.BreakpointName.XL].includes(breakpoint) ? (
        <td key="vault">
          <Link to={depositLink} className="underline" target={linkTarget}>
            {vaultIcon}
            <div className="coin-name">
              {`${baseToken.displayName}-${rewardToken.displayName}`}
            </div>
          </Link>
          {farm.chainId !== chainId && (
            <div className="sub-name">{farmNetworkIndicator}</div>
          )}
        </td>
      ) : (
        <td key="vault">
          {vaultIcon}
          <OptionalOutboundLink
            href={dexLink}
          >
            <div className="coin-name">
              {`${baseToken.displayName}-${rewardToken.displayName}`}
              <Image className="icon-new-window" alt="new window icon" />
            </div>
          </OptionalOutboundLink>
          {farm.chainId !== chainId && (
            <div className="sub-name">{farmNetworkIndicator}</div>
          )}
        </td>
      )}

      {/* Liquidity */}
      <td key="liquidity" className="primary-text-color">
        <FormatFloat value={farm.tvl.toString()} prefix="$" />
        {farm.tokens.map((token) => (
          <div key={token.name}>
            {token.reserve > 0 && (
              <div className="sub-text">
                <FormatFloat
                  value={token.reserve.toString()}
                  postfix={` ${token.displayName}`}
                  displayDecimals={token.reserve > 10 ? 0 : 2}
                />
              </div>
            )}
          </div>
        ))}
      </td>

      {/* Actions Column */}
      {[ScreenUtils.BreakpointName.XL].includes(breakpoint) && (
        <td key="actions" className="w-44">
          <VaultActionButton farm={farm} vault={monitorVault} />
        </td>
      )}
    </tr>
  );
};

export default VaultsInfoTablePosition;
